"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConversationsClient = void 0;
const utils_1 = require("../../utils");
let fs;
if (typeof window === "undefined") {
    fs = require("fs");
}
class ConversationsClient {
    constructor(client) {
        this.client = client;
    }
    /**
     * Create a new conversation.
     * @param name The name of the conversation
     * @returns The created conversation
     */
    async create(options) {
        const data = Object.assign({}, ((options === null || options === void 0 ? void 0 : options.name) && { name: options === null || options === void 0 ? void 0 : options.name }));
        return this.client.makeRequest("POST", "conversations", {
            data,
        });
    }
    /**
     * List conversations with pagination and sorting options.
     * @param ids List of conversation IDs to retrieve
     * @param offset Specifies the number of objects to skip. Defaults to 0.
     * @param limit Specifies a limit on the number of objects to return, ranging between 1 and 100. Defaults to 100.
     * @returns A list of conversations
     */
    async list(options) {
        var _a, _b;
        const params = {
            offset: (_a = options === null || options === void 0 ? void 0 : options.offset) !== null && _a !== void 0 ? _a : 0,
            limit: (_b = options === null || options === void 0 ? void 0 : options.limit) !== null && _b !== void 0 ? _b : 100,
        };
        if ((options === null || options === void 0 ? void 0 : options.ids) && options.ids.length > 0) {
            params.ids = options.ids;
        }
        return this.client.makeRequest("GET", "conversations", {
            params,
        });
    }
    /**
     * Get detailed information about a specific conversation.
     * @param id The ID of the conversation to retrieve
     * @returns The conversation
     */
    async retrieve(options) {
        return this.client.makeRequest("GET", `conversations/${options.id}`);
    }
    /**
     * Update an existing conversation.
     * @param id The ID of the conversation to update
     * @param name The new name of the conversation
     * @returns The updated conversation
     */
    async update(options) {
        const data = {
            name: options.name,
        };
        return this.client.makeRequest("POST", `conversations/${options.id}`, {
            data,
        });
    }
    /**
     * Delete a conversation.
     * @param id The ID of the conversation to delete
     * @returns Whether the conversation was successfully deleted
     */
    async delete(options) {
        return this.client.makeRequest("DELETE", `conversations/${options.id}`);
    }
    /**
     * Add a new message to a conversation.
     * @param id The ID of the conversation to add the message to
     * @param content The content of the message
     * @param role The role of the message (e.g., "user" or "assistant")
     * @param parentID The ID of the parent message
     * @param metadata Additional metadata to attach to the message
     * @returns The created message
     */
    async addMessage(options) {
        const data = Object.assign(Object.assign({ content: options.content, role: options.role }, (options.parentID && { parentID: options.parentID })), (options.metadata && { metadata: options.metadata }));
        return this.client.makeRequest("POST", `conversations/${options.id}/messages`, {
            data,
        });
    }
    /**
     * Update an existing message in a conversation.
     * @param id The ID of the conversation containing the message
     * @param messageID The ID of the message to update
     * @param content The new content of the message
     * @param metadata Additional metadata to attach to the message
     * @returns The updated message
     */
    async updateMessage(options) {
        const data = Object.assign(Object.assign({}, (options.content && { content: options.content })), (options.metadata && { metadata: options.metadata }));
        return this.client.makeRequest("POST", `conversations/${options.id}/messages/${options.messageID}`, {
            data,
        });
    }
    /**
     * Export conversations as a CSV file with support for filtering and column selection.
     *
     * @param options Export configuration options
     * @param options.outputPath Path where the CSV file should be saved (Node.js only)
     * @param options.columns Optional list of specific columns to include
     * @param options.filters Optional filters to limit which conversations are exported
     * @param options.includeHeader Whether to include column headers (default: true)
     * @returns Promise<Blob> in browser environments, Promise<void> in Node.js
     */
    async export(options = {}) {
        var _a;
        const data = {
            include_header: (_a = options.includeHeader) !== null && _a !== void 0 ? _a : true,
        };
        if (options.columns) {
            data.columns = options.columns;
        }
        if (options.filters) {
            data.filters = options.filters;
        }
        const response = await this.client.makeRequest("POST", "conversations/export", {
            data,
            responseType: "arraybuffer",
            headers: { Accept: "text/csv" },
        });
        // Node environment
        if (options.outputPath && typeof process !== "undefined") {
            await fs.promises.writeFile(options.outputPath, Buffer.from(response));
            return;
        }
        // Browser
        return new Blob([response], { type: "text/csv" });
    }
    /**
     * Export users as a CSV file and save it to the user's device.
     * @param filename
     * @param options
     */
    async exportToFile(options) {
        const blob = await this.export(options);
        if (blob instanceof Blob) {
            (0, utils_1.downloadBlob)(blob, options.filename);
        }
    }
    /**
     * Export messages as a CSV file with support for filtering and column selection.
     *
     * @param options Export configuration options
     * @param options.outputPath Path where the CSV file should be saved (Node.js only)
     * @param options.columns Optional list of specific columns to include
     * @param options.filters Optional filters to limit which messages are exported
     * @param options.includeHeader Whether to include column headers (default: true)
     * @returns Promise<Blob> in browser environments, Promise<void> in Node.js
     */
    async exportMessages(options = {}) {
        var _a;
        const data = {
            include_header: (_a = options.includeHeader) !== null && _a !== void 0 ? _a : true,
        };
        if (options.columns) {
            data.columns = options.columns;
        }
        if (options.filters) {
            data.filters = options.filters;
        }
        const response = await this.client.makeRequest("POST", "conversations/export_messages", {
            data,
            responseType: "arraybuffer",
            headers: { Accept: "text/csv" },
        });
        // Node environment
        if (options.outputPath && typeof process !== "undefined") {
            await fs.promises.writeFile(options.outputPath, Buffer.from(response));
            return;
        }
        // Browser
        return new Blob([response], { type: "text/csv" });
    }
    /**
     * Export messages as a CSV file and save it to the user's device.
     * @param filename
     * @param options
     */
    async exportMessagesToFile(options) {
        const blob = await this.exportMessages(options);
        if (blob instanceof Blob) {
            (0, utils_1.downloadBlob)(blob, options.filename);
        }
    }
}
exports.ConversationsClient = ConversationsClient;
//# sourceMappingURL=conversations.js.map